import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { form, FormField, required } from '@angular/forms/signals';
import {
  CreateOptionRequestV1,
  OptionDisplay,
  OptionResponseV1,
  PricingFrequency,
  ProductLine
} from '@membership/membership';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { firstValueFrom } from 'rxjs';

import { DialogMode } from '@core/models/models';
import { MembershipService } from '@core/services/membership';

interface OptionFormModel {
  name: string;
  description: string;
  categoryCode: string;
  shorthandCode: string;
  baselinePrice: number;
  productLine: ProductLine;
  oneTimeFee: number | null;
  pricingFrequency: PricingFrequency;
  isRequired: boolean;
  isDefault: boolean;
  featureIds: string[];
  isActive: boolean;
}

@Component({
  selector: 'app-options-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    DialogModule,
    SelectModule,
    MultiSelectModule,
    IconField,
    InputIcon,
    InputTextModule,
    TextareaModule,
    InputNumberModule,
    FormField,
    ProgressSpinnerModule
  ],
  templateUrl: './options-table.html',
  styleUrl: './options-table.scss'
})
export class OptionsTableComponent {
  private readonly membershipService = inject(MembershipService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  readonly optionsResource = this.membershipService.createOptionsResource();
  readonly featuresResource = this.membershipService.createFeaturesResource();

  readonly searchTerm = signal<string>('');
  readonly showDialog = signal<boolean>(false);
  readonly dialogMode = signal<DialogMode>(DialogMode.Create);
  readonly selectedOptionId = signal<string | null>(null);
  readonly deletingId = signal<string | null>(null);
  readonly saving = signal<boolean>(false);

  readonly optionFormModel = signal<OptionFormModel>(this.emptyOptionForm());

  readonly optionForm = form(this.optionFormModel, (schemaPath) => {
    required(schemaPath.name, { message: 'Option name is required' });
    required(schemaPath.categoryCode, { message: 'Category code is required' });
    required(schemaPath.shorthandCode, { message: 'Shorthand code is required' });
  });

  readonly productLineOptions = signal([
    { label: 'Marketplace', value: ProductLine.Marketplace },
    { label: 'Health', value: ProductLine.Health }
  ]);

  readonly pricingFrequencyOptions = signal([
    { label: 'Once', value: PricingFrequency.Once },
    { label: 'Startup', value: PricingFrequency.Startup },
    { label: 'Monthly', value: PricingFrequency.Monthly },
    { label: 'Quarterly', value: PricingFrequency.Quarterly },
    { label: 'Semi-Annual', value: PricingFrequency.SemiAnnual },
    { label: 'Annual', value: PricingFrequency.Annual }
  ]);

  readonly columns = signal<{ field: string; header: string; sortable: boolean }[]>([
    { field: 'name', header: 'Option Name', sortable: true },
    { field: 'optionCode', header: 'Code', sortable: true },
    { field: 'description', header: 'Description', sortable: false },
    { field: 'baselinePrice', header: 'Price', sortable: true },
    { field: 'features', header: 'Features', sortable: false },
    { field: 'isRequired', header: 'Required', sortable: true },
    { field: 'isDefault', header: 'Default', sortable: true },
    { field: 'isActive', header: 'Status', sortable: true },
    { field: 'actions', header: 'Actions', sortable: false }
  ]);

  readonly filteredOptions = computed<OptionDisplay[]>(() => {
    const searchTerm = this.searchTerm().toLowerCase();
    const options: OptionDisplay[] = this.optionsResource.value() ?? [];
    if (!searchTerm) return options;

    return options.filter(
      (option: OptionDisplay) =>
        option.name.toLowerCase().includes(searchTerm) || option.description?.toLowerCase().includes(searchTerm)
    );
  });

  readonly dialogTitle = computed(() => (this.dialogMode() === DialogMode.Create ? 'Create Option' : 'Edit Option'));

  readonly dialogButtonLabel = computed(() => (this.dialogMode() === DialogMode.Create ? 'Create' : 'Update'));

  readonly isLoading = computed(() => this.optionsResource.isLoading() || this.featuresResource.isLoading());

  readonly features = computed(() => this.featuresResource.value() ?? []);

  readonly selectedOptionCode = computed<string | null>(() => {
    const id = this.selectedOptionId();
    if (!id) return null;
    const options = this.optionsResource.value() ?? [];
    return options.find((o) => o.optionId === id)?.optionCode ?? null;
  });

  readonly isEditMode = computed(() => this.dialogMode() === DialogMode.Edit);

  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.set(value);
  }

  onCreateOption(): void {
    this.dialogMode.set(DialogMode.Create);
    this.selectedOptionId.set(null);
    this.optionFormModel.set(this.emptyOptionForm());
    this.showDialog.set(true);
  }

  async onEditOption(option: OptionDisplay): Promise<void> {
    this.dialogMode.set(DialogMode.Edit);
    this.selectedOptionId.set(option.optionId);

    const baseModel: OptionFormModel = {
      name: option.name,
      description: option.description || '',
      categoryCode: option.categoryCode,
      shorthandCode: option.shorthandCode,
      baselinePrice: option.baselinePrice,
      productLine: option.productLine,
      oneTimeFee: option.oneTimeFee ?? null,
      pricingFrequency: option.pricingFrequency,
      isRequired: option.isRequired,
      isDefault: option.isDefault,
      featureIds: [],
      isActive: option.isActive
    };

    try {
      const optionFeatures = await firstValueFrom(this.membershipService.getOptionFeatures(option.optionId));
      this.optionFormModel.set({
        ...baseModel,
        featureIds: optionFeatures.map((feat) => feat.featureId)
      });
    } catch (error) {
      console.error('Failed to load option features:', error);
      this.optionFormModel.set(baseModel);
    }
    this.showDialog.set(true);
  }

  async onSaveOption(): Promise<void> {
    if (!this.isFormValid()) {
      this.showError('Please fix validation errors');
      return;
    }

    this.saving.set(true);
    const formData = this.optionFormModel();
    const payload: CreateOptionRequestV1 = {
      name: formData.name,
      description: formData.description || undefined,
      categoryCode: formData.categoryCode,
      shorthandCode: formData.shorthandCode,
      baselinePrice: formData.baselinePrice,
      productLine: formData.productLine,
      oneTimeFee: formData.oneTimeFee ?? undefined,
      pricingFrequency: formData.pricingFrequency,
      isRequired: formData.isRequired,
      isDefault: formData.isDefault,
      isActive: formData.isActive,
      featureIds: formData.featureIds
    };

    const operation =
      this.dialogMode() === DialogMode.Create
        ? this.membershipService.createOption(payload)
        : this.membershipService.updateOption(this.selectedOptionId()!, payload as Partial<OptionResponseV1>);

    try {
      await firstValueFrom(operation);
      this.showSuccess(`Option ${this.dialogMode() === DialogMode.Create ? 'created' : 'updated'} successfully`);
      this.showDialog.set(false);
      this.optionsResource.reload();
    } catch (error) {
      this.showError('Failed to save option');
      console.error('Save option error:', error);
    } finally {
      this.saving.set(false);
    }
  }

  onDeleteOption(option: OptionDisplay): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete option "${option.name}"?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.deleteOption(option)
    });
  }

  private async deleteOption(option: OptionDisplay): Promise<void> {
    this.deletingId.set(option.optionId);

    try {
      await firstValueFrom(this.membershipService.deleteOption(option.optionId));
      this.showSuccess(`Option ${option.name} deleted successfully`);
      this.optionsResource.reload();
    } catch (error) {
      this.showError('Failed to delete option');
      console.error('Delete option error:', error);
    } finally {
      this.deletingId.set(null);
    }
  }

  isFormValid(): boolean {
    const model = this.optionFormModel();
    return !!(
      model.name.trim() &&
      model.categoryCode.trim() &&
      model.shorthandCode.trim() &&
      model.baselinePrice >= 0 &&
      this.optionForm.name().valid() &&
      this.optionForm.categoryCode().valid() &&
      this.optionForm.shorthandCode().valid()
    );
  }

  onFeaturesChange(featureIds: string[]) {
    this.optionFormModel.set({
      ...this.optionFormModel(),
      featureIds
    });
  }

  onProductLineChange(productLine: ProductLine) {
    this.optionFormModel.set({
      ...this.optionFormModel(),
      productLine
    });
  }

  onPricingFrequencyChange(pricingFrequency: PricingFrequency) {
    this.optionFormModel.set({
      ...this.optionFormModel(),
      pricingFrequency
    });
  }

  formatCurrency(amount: number | null | undefined): string {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  getStatusSeverity(isActive: boolean): 'success' | 'danger' {
    return isActive ? 'success' : 'danger';
  }

  getStatusLabel(isActive: boolean): string {
    return isActive ? 'Active' : 'Inactive';
  }

  getRequiredSeverity(isRequired: boolean): 'warn' | 'secondary' {
    return isRequired ? 'warn' : 'secondary';
  }

  getRequiredLabel(isRequired: boolean): string {
    return isRequired ? 'Required' : 'Optional';
  }

  private emptyOptionForm(): OptionFormModel {
    return {
      name: '',
      description: '',
      categoryCode: '',
      shorthandCode: '',
      baselinePrice: 0,
      productLine: ProductLine.Marketplace,
      oneTimeFee: null,
      pricingFrequency: PricingFrequency.Once,
      isRequired: false,
      isDefault: false,
      featureIds: [],
      isActive: true
    };
  }

  private showError(message: string): void {
    this.messageService.add({ severity: 'error', summary: 'Error', detail: message });
  }

  private showSuccess(message: string): void {
    this.messageService.add({ severity: 'success', summary: 'Success', detail: message });
  }
}
